import resourceInfinit from '../../../vendor/lacodix/resourcerer/resources/js/components/resourceInfinit'
import resourceTable from '../../../vendor/lacodix/resourcerer/resources/js/components/resourceTable'

// The event list owns its membership-aware refresh. Avoid the package's parallel
// ID-only patch, which deliberately ignores optional filters and search.
export const eventOverviewTable = () => {
  const table = resourceTable()
  table.onSoftRefresh = () => {}
  return table
}

export default (opts = {}) => {
  const resource = opts.resource ?? 'events'
  const refreshMethod = opts.refreshMethod ?? 'refreshEventRows'
  const historyAttribute = `data-${opts.historyKey ?? 'event'}-history`
  const cursorsAttribute = `data-${opts.historyKey ?? 'event'}-cursors`
  let root = null
  let wireId = null
  let active = false
  const cleanups = []

  return {
    ...resourceInfinit(opts),
    refreshing: false,
    rowCount: 0,
    refreshPending: null,
    pendingDirection: null,

    init() {
      root = this.$el
      wireId = root.closest('[wire\\:id]')?.getAttribute('wire:id')
      // Livewire only snapshots inner scroll containers that opt into this
      // directive. Keep this opt-in on the event surface rather than changing
      // scroll restoration for every application page.
      this._scrollContainer().setAttribute('wire:navigate:scroll', '')
      active = true
      const restored = root.hasAttribute(historyAttribute)
      const cursors = root.getAttribute(cursorsAttribute)
      if (cursors) {
        const state = JSON.parse(cursors)
        this.prev = state.prev
        this.next = state.next
      }
      root.removeAttribute(historyAttribute)
      this.syncListState()

      const listen = (target, name, handler) => {
        target.addEventListener(name, handler)
        cleanups.push(() => target.removeEventListener(name, handler))
      }
      const leave = () => {
        this.syncListState()
        root.setAttribute(historyAttribute, '')
        active = false
        this._invalidateEndlessRequests()
      }
      listen(document, 'livewire:navigating', leave)
      listen(window, 'pagehide', leave)
      listen(window, 'pageshow', event => {
        if (!event.persisted) return
        active = true
        this.refresh()
      })
      listen(window, 'resourcerer:soft-refresh', event => {
        if (event.detail?.resource && event.detail.resource !== resource) return
        this.refresh()
      })
      if (restored) this.$nextTick(() => { this.refresh() })
    },

    destroy() {
      active = false
      this._invalidateEndlessRequests()
      cleanups.splice(0).forEach(cleanup => cleanup())
    },

    _isTornDown() { return !active || !root?.isConnected },
    _listEl() { return root?.querySelector('[x-ref="list"]') },
    _endlessGenerationRoot() { return root?.closest('.resourcerer-table') },
    _livewire() { return wireId && window.Livewire?.find(wireId) },

    syncListState() {
      this.rowCount = this._listEl()?.querySelectorAll('.resourcerer-row').length ?? 0
      root.setAttribute(cursorsAttribute, JSON.stringify({ prev: this.prev, next: this.next }))
    },

    requestIsCurrent(list, generation) {
      return !this._isTornDown() && list?.isConnected && list === this._listEl() &&
        generation === this._currentEndlessGeneration()
    },

    anchorPositions() {
      const scroller = this._scrollContainer()
      const top = scroller.getBoundingClientRect().top
      return [...this._listEl().querySelectorAll('.resourcerer-row')]
        .filter(row => row.getBoundingClientRect().bottom > top)
        .map(row => ({ id: row.querySelector('[data-row-id]').dataset.rowId, top: row.getBoundingClientRect().top - top }))
    },

    scrollAnchor(rows = this.anchorPositions()) {
      const scroller = this._scrollContainer()
      return () => {
        for (const anchor of rows) {
          const card = this._listEl().querySelector(`[data-row-id="${CSS.escape(anchor.id)}"]`)
          if (!card) continue
          // Membergy's main scroller uses scroll-smooth. Anchoring must be
          // immediate, otherwise prepending animates the reader away and back.
          scroller.scrollTo({
            top: scroller.scrollTop + card.closest('.resourcerer-row').getBoundingClientRect().top -
              scroller.getBoundingClientRect().top - anchor.top,
            behavior: 'instant'
          })
          break
        }
      }
    },

    fragment(html) {
      if (typeof html !== 'string') throw new Error('Missing event fragment')
      const template = document.createElement('template')
      template.innerHTML = html.trim()
      return template.content
    },

    notifyFailure() {
      window.dispatchEvent(new CustomEvent('notify', {
        detail: {
          type: 'danger', title: opts.errorTitle, message: opts.errorMessage
        }
      }))
    },

    // Use the package's cursor endpoint and generation contract, resolving the
    // Livewire host freshly even for queued intersection/refresh continuations.
    async load(direction) {
      if (this._isTornDown()) return
      if (this.refreshing) {
        this.pendingDirection = direction
        return
      }
      if (this.loading) return
      const cursor = direction === 'prev' ? this.prev : this.next
      const list = this._listEl()
      if (!cursor || !list) return
      const generation = this._currentEndlessGeneration()
      const fingerprint = this.queryFingerprint
      let retryDiscardedLoad = false
      this.loading = true
      this.loadingPrev = direction === 'prev'
      try {
        const response = await this._livewire()?.fetchCursorFragment(direction, cursor)
        if (!this.requestIsCurrent(list, generation) || fingerprint !== this.queryFingerprint) {
          retryDiscardedLoad = fingerprint === this.queryFingerprint &&
            this.requestIsCurrent(list, this._currentEndlessGeneration())
          return
        }
        const fragment = this.fragment(response?.html)
        const restoreAnchor = this.scrollAnchor()
        if (opts.deduplicateCursorRows === true) this._removeIncomingDuplicates(fragment, list)
        if (direction === 'prev') {
          list.prepend(fragment)
          this.prev = response.prev ?? null
        } else {
          list.append(fragment)
          this.next = response.next ?? null
        }
        this.syncListState()
        if (direction === 'prev') {
          await window.Alpine.nextTick()
          if (this.requestIsCurrent(list, generation)) restoreAnchor()
        }
      } catch {
        if (this.requestIsCurrent(list, generation)) this.notifyFailure()
      } finally {
        this.loading = false
        this.loadingPrev = false
        if (this.refreshPending === this._currentEndlessGeneration()) {
          if (retryDiscardedLoad) this.pendingDirection = direction
          this.refresh()
        } else if (retryDiscardedLoad) {
          // A same-query morph leaves the sentinel visible, so no new
          // intersection will fire. Continue with the package's current cursor.
          this._retryDiscardedEndlessLoad(direction)
        }
      }
    },

    async refresh() {
      if (this._isTornDown()) return
      if (this.loading) {
        this.refreshPending = this._currentEndlessGeneration()
        return
      }
      this.refreshPending = null
      this.refreshing = true
      this.loading = true
      const list = this._listEl()
      const generation = this._currentEndlessGeneration()
      const ids = [...list.querySelectorAll('[data-row-id]')].map(row => row.dataset.rowId)
      try {
        const response = await this._livewire()?.[refreshMethod](ids)
        if (!this.requestIsCurrent(list, generation)) return
        const fragment = this.fragment(response?.html)
        if (!Array.isArray(response.missing)) throw new Error('Missing event membership')
        const restoreAnchor = this.scrollAnchor()
        const Alpine = window.Alpine
        Alpine.mutateDom(() => {
          if (!ids.length) {
            list.replaceChildren(fragment)
            Alpine.initTree(list)
            this.prev = response.prev ?? null
            this.next = response.next ?? null
          } else {
            for (const row of fragment.querySelectorAll('.resourcerer-row')) {
              const id = row.querySelector('[data-row-id]').dataset.rowId
              const current = list.querySelector(`[data-row-id="${CSS.escape(id)}"]`)?.closest('.resourcerer-row')
              if (!current) continue
              Alpine.destroyTree(current)
              current.replaceWith(row)
              Alpine.initTree(row)
            }
            for (const id of response.missing) {
              const current = list.querySelector(`[data-row-id="${CSS.escape(String(id))}"]`)?.closest('.resourcerer-row')
              if (!current) continue
              Alpine.destroyTree(current)
              current.remove()
            }
          }
        })
        this.syncListState()
        await window.Alpine.nextTick()
        if (this.requestIsCurrent(list, generation)) restoreAnchor()
      } catch {
        if (this.requestIsCurrent(list, generation)) this.notifyFailure()
      } finally {
        this.loading = false
        this.refreshing = false
        if (!this._isTornDown()) {
          if (this.refreshPending === this._currentEndlessGeneration()) this.refresh()
          else if (this.pendingDirection && this.requestIsCurrent(list, generation)) {
            const direction = this.pendingDirection
            this.pendingDirection = null
            this.load(direction)
          }
        }
      }
    },

    pullStyle() {
      if (!this.isMobile()) return {}
      const offset = this.pulling && this.pullDistance > 0 ? Math.min(this.pullDistance * 0.5, 60) : 0
      return {
        transform: `translateY(${offset}px)`,
        transition: this.pulling ? 'none' : 'transform 0.15s ease-out'
      }
    },

    handlePullStart(event) {
      if (!this.isMobile() || !this.isAtTop() || this.loading) return
      this.pullStartY = event.touches[0].clientY
      this.pullDistance = 0
      this.pulling = false
    },

    async handlePullEnd() {
      const refresh = this.isMobile() && this.isAtTop() && this.pulling && this.pullDistance > 60 && !this.loading
      this.pulling = false
      this.pullStartY = null
      this.pullDistance = 0
      if (refresh) await this.refresh()
    }
  }
}
